import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  Download, 
  ExternalLink, 
  ArrowLeft,
  FileText,
  Calculator,
  Phone,
  Lightbulb,
  Home,
  Users
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const RentersResources = () => {
  const resources = [
    {
      title: "Philadelphia Tenant Resource Guide 2023",
      description: "Complete guide to your rights as a renter in Philadelphia - official 2023 edition",
      type: "PDF Download",
      icon: <FileText className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Download PDF",
      status: "Available",
      link: "https://customer-assets.emergentagent.com/job_c67525ff-89d3-4294-aae1-355dc1b1cc3a/artifacts/phhrqqoq_Philly-Tenant-Resource-Guide-2023-English.pdf"
    },
    {
      title: "Move-In Checklist", 
      description: "Essential checklist for your first day in a new rental - ensure everything is documented",
      type: "Branded Printable Guide",
      icon: <Home className="w-6 h-6" />,
      color: "#87A96B",
      action: "Open Checklist", 
      status: "Available",
      isInteractive: true,
      internalLink: "/resources/renters/move-in-checklist"
    },
    {
      title: "Fair Housing Guide",
      description: "Know your rights - comprehensive guide to fair housing laws and protections",
      type: "PDF Download",
      icon: <Users className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Download PDF",
      status: "Available", 
      link: "https://customer-assets.emergentagent.com/job_c67525ff-89d3-4294-aae1-355dc1b1cc3a/artifacts/f84esptc_Fair-Housing-guide-1.pdf"
    },
    {
      title: "Partners in Good Housing Guide",
      description: "Resource directory and partnership guide for stable, quality housing",
      type: "PDF Download",
      icon: <Phone className="w-6 h-6" />,
      color: "#87A96B",
      action: "Download PDF",
      status: "Available",
      link: "https://customer-assets.emergentagent.com/job_c67525ff-89d3-4294-aae1-355dc1b1cc3a/artifacts/x1vgfl2r_Partners-in-Good-Housing-2025.09.22-FINAL.pdf"
    },
    {
      title: "Rental Budget Calculator",
      description: "Interactive tool to calculate affordable rent based on your income and expenses",
      type: "Interactive Tool",
      icon: <Calculator className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Open Calculator",
      status: "Available",
      isInteractive: true,
      internalLink: "/resources/renters/calculator"
    },
    {
      title: "Budget Worksheet",
      description: "Branded printable worksheet to plan and track your monthly budget in detail",
      type: "Branded Printable Worksheet",
      icon: <FileText className="w-6 h-6" />,
      color: "#87A96B",
      action: "Open Worksheet",
      status: "Available",
      isInteractive: true,
      internalLink: "/resources/renters/budget-worksheet"
    },
    {
      title: "Utility Setup Guide - Philadelphia",
      description: "Step-by-step guide for setting up utilities (PECO, PGW, Water) in your new Philadelphia rental",
      type: "PDF Guide",
      icon: <Lightbulb className="w-6 h-6" />,
      color: "#3F5D4A", 
      action: "Download Guide",
      status: "Available",
      link: "https://customer-assets.emergentagent.com/job_rental-guidance/artifacts/15r3onbw_Utility_Setup_Guide_Philadelphia_Dorsey_Taylor_Homes.pdf"
    },
    {
      title: "Year-Round Home Maintenance Guide",
      description: "Robust seasonal checklists, monthly quick-checks, fix-it-yourself vs. call-the-landlord guidance, and emergency contacts — all in one branded printable guide",
      type: "Branded Printable Guide",
      icon: <Home className="w-6 h-6" />,
      color: "#87A96B", 
      action: "Open Maintenance Guide",
      status: "Available",
      isInteractive: true,
      internalLink: "/resources/renters/maintenance-guide"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F4ED]">
      <Header />
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          
          {/* Back Navigation */}
          <div className="mb-8">
            <Link to="/resources" className="inline-flex items-center text-gray-600 hover:text-[#3F5D4A] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resource Center
            </Link>
          </div>

        {/* Header */}
        <div className="text-center space-y-6 mb-16">
          <div 
            className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold"
            style={{backgroundColor: '#3F5D4A'}}
          >
            START HERE
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
            Resources for <span style={{color: '#3F5D4A'}}>Renters</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Essential tools and guides for successful renting, understanding your rights, 
            and building stability for your future.
          </p>
        </div>

        {/* Resources Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {resources.map((resource, index) => (
            <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{backgroundColor: resource.color + '20'}}
                  >
                    {React.cloneElement(resource.icon, { 
                      style: { color: resource.color } 
                    })}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-xl font-bold text-gray-800">{resource.title}</CardTitle>
                      <span 
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          resource.status === 'Available' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {resource.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{resource.type}</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-gray-600 mb-6 leading-relaxed">{resource.description}</p>
                
                {resource.isInteractive ? (
                  <Link to={resource.internalLink}>
                    <Button 
                      className="w-full text-white font-semibold py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                      style={{backgroundColor: resource.color}}
                    >
                      {React.cloneElement(resource.icon, { className: "w-4 h-4 mr-2", style: { color: "#fff" } })}
                      {resource.action}
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    className="w-full text-white font-semibold py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{backgroundColor: resource.color}}
                    disabled={resource.status === 'Coming Soon'}
                    onClick={() => {
                      if (resource.link) {
                        window.open(resource.link, '_blank');
                      }
                    }}
                  >
                    {resource.status === 'Coming Soon' ? (
                      <>Coming Soon</>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {resource.action}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F0EBE0] border border-[#D4C9B0] rounded-lg p-6 mb-16">
          <h3 className="font-semibold text-[#3F5D4A] mb-2">Important Note</h3>
          <p className="text-[#5C5852] text-sm">
            All resources are provided for informational purposes only. For specific legal advice, 
            please consult with a qualified attorney or housing counselor. Emergency situations 
            should always be directed to local authorities or emergency services.
          </p>
        </div>

        {/* Next Steps */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-8 md:p-12 text-white"
            style={{background: '#3F5D4A'}}
          >
            <h3 className="text-3xl font-bold mb-4">Ready for the Next Step?</h3>
            <p className="text-xl mb-6 opacity-90">
              Feeling confident about renting? Explore our Rent Readiness Program or homeowner resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs">
                <Button 
                  className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg"
                  style={{
                    background: '#87A96B',
                    borderColor: '#87A96B'
                  }}
                >
                  Join Our Program
                </Button>
              </Link>
              <Link to="/resources/homeowners">
                <Button 
                  variant="outline"
                  className="border-2 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                  style={{
                    borderColor: '#FEFEFE',
                    color: '#FEFEFE',
                    backgroundColor: 'transparent'
                  }}
                >
                  Homeowner Resources
                </Button>
              </Link>
            </div>
          </div>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RentersResources;